import { celNewHire } from './forms/cel-1048-new-hire';
import { clientOnboarding } from './forms/client-onboarding';
import { createEntraUser } from './forms/create-entra-user';
import { genericRequest } from './forms/generic-request';
import { updateEntraUser } from './forms/update-entra-user';
import type { FormAudience, FormDefinition, FormDestination } from './types';

/**
 * Every form the portal knows about.
 *
 * Adding a form = create `forms/<id>.ts` with `defineForm(...)`, then add it here.
 * Nothing else in the codebase needs to change: the web app renders whatever is
 * listed, and the server validates against the same objects.
 */
const forms = [
  genericRequest,
  celNewHire,
  createEntraUser,
  updateEntraUser,
  clientOnboarding,
] as const;

/** Union of all registered form ids, e.g. `'generic-request' | 'onboard-user'`. */
export type FormId = (typeof forms)[number]['id'];

export const registry: ReadonlyMap<string, FormDefinition> = new Map<string, FormDefinition>(
  forms.map((form) => [form.id, form]),
);

/** What a client needs to show a list of forms — no schema, no field hints. */
export interface FormSummary {
  id: string;
  version: string;
  title: string;
  description?: string | undefined;
  /** Who this form is for: every allowlisted tenant, or named customer numbers. */
  customers: FormAudience;
  /** Where its submissions go. Always stated here, even when the form left it implicit. */
  destination: FormDestination;
}

/** A form's destination, with the default made explicit: unset means a case. */
export function destinationOf(form: Pick<FormDefinition, 'destination'>): FormDestination {
  return form.destination ?? 'cases';
}

export function listForms(): FormSummary[] {
  return forms.map((form) => ({
    id: form.id,
    version: form.version,
    title: form.title,
    description: form.description,
    customers: form.customers,
    destination: destinationOf(form),
  }));
}

export function getForm(id: string): FormDefinition | undefined {
  return registry.get(id);
}
